"use client";

import { useMemo } from "react";
import { motion } from "framer-motion";
import { getConstellationNodes } from "@/lib/atlas/constellationLayout";

export interface ConstellationProps {
  nodeCount: number;
  primaryColor: string;
  secondaryColor: string;
  phase: "ignition" | "birth" | "sync" | "collapse";
  collapseProgress?: number;
  reducedMotion?: boolean;
  className?: string;
}

/**
 * Unique constellation: core + spiral, alive with pulse and subtle float.
 */
export default function Constellation({
  nodeCount,
  primaryColor,
  secondaryColor,
  phase,
  collapseProgress = 0,
  reducedMotion = false,
  className = "",
}: ConstellationProps) {
  const nodes = useMemo(() => getConstellationNodes(nodeCount), [nodeCount]);

  if (reducedMotion) {
    return (
      <div className={`absolute inset-0 flex items-center justify-center pointer-events-none ${className}`} style={{ top: "10%" }}>
        <div
          className="rounded-full opacity-70"
          style={{
            width: 14,
            height: 14,
            background: `radial-gradient(circle, ${primaryColor} 0%, ${secondaryColor} 100%)`,
            boxShadow: `0 0 24px ${primaryColor}50`,
          }}
        />
      </div>
    );
  }

  const isCollapsing = phase === "collapse";
  const showNode = (i: number) => phase !== "ignition" || i < 2;
  const cx = 50;
  const cy = 38;

  return (
    <div className={`absolute inset-0 pointer-events-none ${className}`} aria-hidden="true">
      {nodes.map((node, i) => {
        const delay = phase === "birth" ? i * 50 : 0;
        const collapseX = cx - node.x;
        const collapseY = cy - node.y;
        const scale = isCollapsing ? 1 - collapseProgress : 1;
        const x = isCollapsing ? node.x + collapseX * collapseProgress : node.x;
        const y = isCollapsing ? node.y + collapseY * collapseProgress : node.y;
        const isSyncAlive = phase === "sync" && !isCollapsing && showNode(i);
        return (
          <motion.div
            key={node.id}
            className={`absolute rounded-full ${isSyncAlive ? "atlas-node-pulse atlas-node-float" : ""}`}
            style={{
              left: `${x}%`,
              top: `${y}%`,
              width: `${Math.max(4, node.r * 2.2)}%`,
              height: `${Math.max(4, node.r * 2.2)}%`,
              transform: "translate(-50%, -50%)",
              background: `radial-gradient(circle at 35% 35%, ${primaryColor}, ${secondaryColor} 70%, ${primaryColor}99)`,
              ...(isSyncAlive ? {} : { boxShadow: `0 0 ${node.r * 2.5}px ${primaryColor}60` }),
              ["--atlas-node-glow" as string]: `${primaryColor}`,
            }}
            initial={phase === "birth" ? { scale: 0.4, opacity: 0 } : false}
            animate={{
              scale: showNode(i) ? scale : 0.4,
              opacity: showNode(i) ? 0.95 : 0,
            }}
            transition={{
              duration: phase === "birth" ? 0.4 : 0.22,
              delay: delay / 1000,
              ease: [0.25, 0.46, 0.45, 0.94],
            }}
          />
        );
      })}
    </div>
  );
}
